import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import ConfirmDialog from "../../shared/ConfirmDialog";

export interface Goal {
  id: string;
  name: string;
}

export interface MoveTargetPanelProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback fired when the dialog is dismissed without moving. */
  onClose: () => void;
  /** Name of the target being moved, shown in the description. */
  targetName: string;
  /**
   * Goals available as destinations.
   * Caller must pre-filter to: same behavior_subject, same goal_type, not archived.
   */
  availableGoals: Goal[];
  /**
   * Called with the selected destination goal ID when the user confirms.
   * The caller is responsible for the move mutation and passing `isLoading`.
   */
  onMove: (destinationGoalId: string) => void;
  /** Shows a spinner on the Move Target button and disables all actions. */
  isLoading?: boolean;
}

/**
 * Dialog for moving a target to a different goal. The BCBA selects the
 * destination from a pre-filtered list of compatible goals. The "Move Target"
 * button stays disabled until a destination is selected.
 *
 * The caller is responsible for filtering `availableGoals` to valid destinations
 * (same behavior_subject, same goal_type, not archived).
 *
 * @param props
 * @param props.open            - Controls dialog visibility
 * @param props.onClose         - Called when the user dismisses without moving
 * @param props.targetName      - Name of the target shown in the description
 * @param props.availableGoals  - Pre-filtered list of valid destination goals
 * @param props.onMove          - Called with destinationGoalId on confirm
 * @param props.isLoading       - Disables actions while the move mutation runs
 */
const MoveTargetPanel: React.FC<MoveTargetPanelProps> = ({
  open,
  onClose,
  targetName,
  availableGoals,
  onMove,
  isLoading = false,
}) => {
  const [destinationGoalId, setDestinationGoalId] = useState("");

  // Reset selection every time the dialog opens.
  useEffect(() => {
    if (open) setDestinationGoalId("");
  }, [open]);

  const handleChange = (e: SelectChangeEvent) => {
    setDestinationGoalId(e.target.value);
  };

  const handleMove = () => {
    if (destinationGoalId) onMove(destinationGoalId);
  };

  const hasDestinations = availableGoals.length > 0;

  return (
    <ConfirmDialog
      open={open}
      title="Move Target"
      confirmLabel="Move Target"
      onClose={onClose}
      onConfirm={handleMove}
      isLoading={isLoading}
      confirmDisabled={!hasDestinations || !destinationGoalId}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {hasDestinations ? (
          <>
            {/* Description */}
            <Typography variant="body1" color="text.secondary">
              Move &ldquo;{targetName}&rdquo; to another goal.
            </Typography>

            {/* Destination goal select */}
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  typography: "caption",
                  fontWeight: 500,
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Destination Goal
              </FormLabel>
              <Select
                displayEmpty
                value={destinationGoalId}
                onChange={handleChange}
                size="small"
                inputProps={{ "aria-label": "Select destination goal" }}
                renderValue={(value) =>
                  value ? (
                    availableGoals.find((g) => g.id === value)?.name ?? value
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Select a goal...
                    </Typography>
                  )
                }
                sx={{ borderRadius: "10px" }}
              >
                {availableGoals.map((goal) => (
                  <MenuItem key={goal.id} value={goal.id}>
                    {goal.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          /* Empty state — no compatible destination goals */
          <Box>
            <Typography component="p" variant="body1" color="text.secondary">
              There are no compatible goals to move &ldquo;{targetName}&rdquo; to.
            </Typography>
            <Typography component="p" variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              A destination goal must have the same goal type and behavior subject, and not be
              archived.
            </Typography>
          </Box>
        )}
      </Box>
    </ConfirmDialog>
  );
};

export default MoveTargetPanel;
